/****************************************************
 * Module Name:  StoneDetailSellPotential.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useState, useEffect } from "react";
import Utility from "../../utility/utility";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import NoPermission from "../common/noPermission";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import renderCellExpand from "../common/dataGridCellExpand";
import { Box, Container, Tooltip, Typography } from "@mui/material";
import TabMenuSnackbar from "../common/tabMenuSnackbar";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { format } from 'date-fns';
import { styled } from "@mui/material/styles";
import WolzaIndiaExportProxy from "../../utility/proxy/nivodautility/wolzaIndiaExportProxy";

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    '& .MuiDataGrid-columnHeader': {
        color: '#fff',
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
        backgroundColor: 'grey',
    },
    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
        py: "8px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
        py: "15px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    },
    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
        py: "22px",
        fontSize: Constants.defaultTableFontSize,
        fontWeight: Constants.defaultFontWeight,
    }
}));


export default function StoneDetailSellPotential(stoneid) {

    const [stonePotentialListLoader, setStonePotentialListLoader] = useState(false);
    const [data, setData] = useState([]);
    const [age, setAge] = useState("");
    const [successSnackbar, setSuccessSnackbar] = useState(false);
    const [errorSnackbar, setErrorSnackbar] = useState(false);

    // eslint-disable-next-line
    const [stoneDetailSellPotential, setStoneDetailSellPotential] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.STONEDETAIL_SELL_POTENTIAL
        )
    );

    useEffect(() => {
        getSingleStoneDetail();
    }, []);

    const getSingleStoneDetail = () => {
        setStonePotentialListLoader(true);
        StoneProxy.GetSingleStone(
            stoneid.stoneid,
            (responseData) => {
                if (responseData) {
                    setAge(responseData.age);
                    handleSearch(responseData.age);
                } else {
                    setStonePotentialListLoader(false);
                }
            }
        );
    }

    const handleSearch = (age) => {
        setStonePotentialListLoader(true);
        let data = {
            from: (age !== null && age !== "") ?
                Utility.ConvertLocalTimeToUtc(new Date(age)).slice(0, 11) + "00:00:00.000Z" : null, // stone potential will be fetched since the stone has been added
            to: Utility.ConvertLocalTimeToUtc(new Date()).slice(0, 11) + "23:59:59.999Z",
        }
        WolzaIndiaExportProxy.GetMarketStonePotentialForStoneForMappedUser(stoneid.stoneid, data, "-1",
            (responseData) => {
                if (responseData) {
                    setData(responseData);
                }
                setStonePotentialListLoader(false);
            });
    };

    const formattedFromDate = age ? format(new Date(age), 'dd-MM-yyyy') : 'N/A';
    const formattedToDate = format(new Date(), 'dd-MM-yyyy');

    const initialColumns = () => [
        {
            field: "userid",
            headerName: "CUSTOMERID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "username",
            headerName: "USERNAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "usercompanyname",
            headerName: "COMPANY NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "userfirstname",
            headerName: "FIRST NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "userlastname",
            headerName: "LAST NAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "useremail",
            headerName: "EMAIL",
            headerAlign: "center",
            align: "center",
            width: 160,
            renderCell: renderCellExpand,
        },
        {
            field: "userphone",
            headerName: "PHONE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "employeeid",
            headerName: "EMPLOYEEID",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "employeeusername",
            headerName: "EMP USERNAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "employeefirstname",
            headerName: "EMP FIRSTNAME",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "employeecompanyname",
            headerName: "EMP COMPANYNAME",
            headerAlign: "center",
            align: "center",
            width: 150,
            renderCell: renderCellExpand,
        },
        {
            field: "employeeemail",
            headerName: "EMP EMAIL",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "employeephone",
            headerName: "EMP PHONE",
            headerAlign: "center",
            align: "center",
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "sellcount",
            headerName: "SELL COUNT",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "quantity",
            headerName: "QTY",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "sumofestimatedfob",
            headerName: "SUM OF FOB",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: renderCellExpand,
        },
        {
            field: "avgppc",
            headerName: "AVG PPC",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 120,
            renderCell: renderCellExpand,
        },
    ]

    const columns = initialColumns();

    const allowed = Utility.isReadAllowed(stoneDetailSellPotential);

    return (
        !allowed ? <NoPermission /> :
            <div>
                <Container maxWidth="xxl">

                    <TabMenuSnackbar
                        show={successSnackbar}
                        message={'Success'}
                        variant={"success"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Success"}
                        handleClose={() => setSuccessSnackbar(false)}
                    />
                    <TabMenuSnackbar
                        show={errorSnackbar}
                        message={Constants.APiFailErrorMessage}
                        variant={"error"}
                        autoHide={Constants.SnackBarCloseTimer}
                        title={"Failed"}
                        handleClose={() => setErrorSnackbar(false)}
                    />
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            color: '#000',
                            width: "100%",
                            position: "relative",
                            "& .MuiTablePagination-selectLabel": {
                                marginTop: "15px",
                            },
                            "& .MuiTablePagination-displayedRows": {
                                marginTop: "15px",
                            },
                            "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                fontWeight: Constants.defaultFontWeightBold,
                            },
                            "& .css-cc8tf1": {
                                fontWeight: Constants.defaultFontWeightBold,
                            },
                            height: '40vh'
                        }}
                    >
                        <StyledDataGrid
                            disableRowSelectionOnClick={true}
                            localeText={{ noRowsLabel: "No Record Found" }}
                            sx={{ height: "40vh", fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight, }}
                            slots={{
                                toolbar: GridToolbar,
                            }}
                            rows={data}
                            density="compact"
                            columns={columns}
                            getRowId={(row) => row.userid}
                            loading={stonePotentialListLoader}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        // Hide columns id, the other columns will remain visible
                                        userid: false,
                                        employeeid: false,
                                    },
                                },
                                pagination: {
                                    paginationModel: Constants.pageSize,
                                },
                            }}
                            sortModel={[
                                {
                                    field: 'count',
                                    sort: 'desc',
                                },
                            ]}
                            pageSizeOptions={Constants.pageSizeOptions}
                            pagination
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: {
                                        debounceMs: 500,
                                    },
                                },
                            }}
                        />
                        <Box
                            style={{
                                position: "absolute",
                                top: 6,
                                right: "265px",
                                display: "flex",
                            }}
                        >
                            <Box sx={{ marginRight: "360px" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                    Potential Sell: {formattedFromDate} to {formattedToDate}
                                </Typography>
                            </Box>

                            {stonePotentialListLoader ? (
                                <CircularProgress size="1rem" />
                            ) : (
                                <Tooltip title="Reload List">
                                    <RefreshOutlinedIcon
                                        onClick={() => handleSearch(age)}
                                        color="primary"
                                        fontSize="large"
                                        sx={{ cursor: "pointer", height: "1.8rem", width: "1.8rem" }}
                                    />
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Container>
            </div>
    );
}